import { createSlice } from '@reduxjs/toolkit';
import { ProfilesDataState } from './profiles-data.state';
import { FacilityTypeOptions } from './models/facility-type-options';
import { GetFacilityTypeOptions } from './thunks/get-facility-type-options.thunk';
import { GetProfileById } from './thunks/get-profile-by-id.thunk';
import { RefreshProfile } from './thunks/refresh-profile.thunk';
import { SelectProfile } from './thunks/select-profile.thunk';
import { UpdateProfile } from './thunks/update-profile.thunk';
import { GetSellerProfileByOpportunityId } from './thunks/get-seller-profile-by-opportunity-id.thunk';
import { UploadPracticeProfileImage } from './thunks/upload-practice-profile-image.thunk';
import { RemovePracticeProfileImage } from './thunks/remove-practice-profile-image.thunk';

function disabledFacilityTypeOptions(): FacilityTypeOptions {
    return { isEnabled: false, options: [] };
}

const initialState: ProfilesDataState = {
    profiles: [],
    facilityTypeOptions: disabledFacilityTypeOptions(),
    facilityTypeOptionsProfileId: '',
    selectedPofile: null,
    isLoading: false,
    status: '',
    error: '',
};

const profilesDataSlice = createSlice({
    name: 'profilesData',
    initialState,
    reducers: {
        resetFacilityTypeOptions: (state) => {
            state.facilityTypeOptions = disabledFacilityTypeOptions();
            state.facilityTypeOptionsProfileId = '';
        },
    },
    extraReducers: (builder) => {
        builder.addCase(GetFacilityTypeOptions.pending, (state, action) => {
            if (action.meta.arg !== state.facilityTypeOptionsProfileId) {
                state.facilityTypeOptions = disabledFacilityTypeOptions();
            }
            state.facilityTypeOptionsProfileId = action.meta.arg;
        });
        builder.addCase(GetFacilityTypeOptions.fulfilled, (state, action) => {
            if (action.meta.arg === state.facilityTypeOptionsProfileId) {
                state.facilityTypeOptions = action.payload;
            }
        });
        builder.addCase(GetFacilityTypeOptions.rejected, (state, action) => {
            if (action.meta.arg === state.facilityTypeOptionsProfileId) {
                state.facilityTypeOptions = disabledFacilityTypeOptions();
            }
        });

        // GetProfileById
        builder.addCase(GetProfileById.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(GetProfileById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.status = 'success';
            // Add profile to profiles array if it doesn't exist
            if (!state.profiles.some((p) => p.id === action.payload.id)) {
                state.profiles.push(action.payload);
            }
            // Update selected profile with fresh data
            state.selectedPofile = action.payload;
        });
        builder.addCase(GetProfileById.rejected, (state, action) => {
            state.isLoading = false;
            state.status = 'failed';
            state.error = action.error.message;
        });

        // SelectProfile
        builder.addCase(SelectProfile.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(SelectProfile.fulfilled, (state, action) => {
            state.isLoading = false;
            state.status = 'success';
            state.selectedPofile = action.payload;
            // Ensure profile is in profiles array
            if (!state.profiles.some((p) => p.id === action.payload?.id)) {
                state.profiles.push(action.payload);
            }
        });
        builder.addCase(SelectProfile.rejected, (state, action) => {
            state.isLoading = false;
            state.status = 'failed';
            state.error = action.error.message;
        });

        // RefreshProfile
        builder.addCase(RefreshProfile.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(RefreshProfile.fulfilled, (state, action) => {
            state.isLoading = false;
            state.status = 'success';
            // Update or add the refreshed profile
            const index = state.profiles.findIndex(
                (p) => p.id === action.payload.id
            );
            if (index !== -1) {
                state.profiles[index] = action.payload;
            } else {
                state.profiles.push(action.payload);
            }
            // Update selected profile if it's the one being refreshed
            if (state.selectedPofile?.id === action.payload.id) {
                state.selectedPofile = action.payload;
            }
        });
        builder.addCase(RefreshProfile.rejected, (state, action) => {
            state.isLoading = false;
            state.status = 'failed';
            state.error = action.error.message;
        });

        // UpdateProfile
        builder.addCase(UpdateProfile.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(UpdateProfile.fulfilled, (state, action) => {
            state.isLoading = false;
            state.status = 'success';
            // Update the profile in the profiles array
            const index = state.profiles.findIndex(
                (p) => p.id === action.payload.id
            );
            if (index !== -1) {
                state.profiles[index] = action.payload;
            }
            // Update selected profile if it's the one being updated
            if (state.selectedPofile?.id === action.payload.id) {
                state.selectedPofile = action.payload;
            }
        });
        builder.addCase(UpdateProfile.rejected, (state, action) => {
            state.isLoading = false;
            state.status = 'failed';
            state.error = action.error.message;
        });

        // GetSellerProfileByOpportunityId
        builder.addCase(GetSellerProfileByOpportunityId.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(
            GetSellerProfileByOpportunityId.fulfilled,
            (state, action) => {
                state.isLoading = false;
                state.status = 'success';
                // Add seller profile to profiles array if it doesn't exist
                if (!state.profiles.some((p) => p.id === action.payload.id)) {
                    state.profiles.push(action.payload);
                }
                // Set as selected profile
                state.selectedPofile = action.payload;
            }
        );
        builder.addCase(
            GetSellerProfileByOpportunityId.rejected,
            (state, action) => {
                state.isLoading = false;
                state.status = 'failed';
                state.error = action.error.message;
            }
        );
        builder.addCase(UploadPracticeProfileImage.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(UploadPracticeProfileImage.fulfilled, (state) => {
            state.isLoading = false;
            state.status = 'success';
        });
        builder.addCase(
            UploadPracticeProfileImage.rejected,
            (state, action) => {
                state.isLoading = false;
                state.status = 'failed';
                state.error = action.error.message;
            }
        );
        builder.addCase(RemovePracticeProfileImage.pending, (state) => {
            state.isLoading = true;
            state.status = 'loading';
            state.error = '';
        });
        builder.addCase(
            RemovePracticeProfileImage.fulfilled,
            (state, action) => {
                state.isLoading = false;
                state.status = 'success';
            }
        );
        builder.addCase(
            RemovePracticeProfileImage.rejected,
            (state, action) => {
                state.isLoading = false;
                state.status = 'failed';
                state.error = action.error.message;
            }
        );
    },
});

export const { resetFacilityTypeOptions } = profilesDataSlice.actions;

export default profilesDataSlice.reducer;
